/**
 * screens/modals/OptionValueModal
 * ──────────────────────────────────────────────────────────────────────────
 * Add a value to one of the five option lists.
 *
 * Values are SLUGGED the same way the API slugs them, and the modal shows the
 * result before saving. Without that, someone types "Card linked" and the chip
 * comes back as `card_linked`, which looks like the app changed their input
 * behind their back. Currencies are uppercased and capped at four characters;
 * approver names are kept exactly as typed, because they are people.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Input, Modal } from '../../components';
import { neutral } from '../../theme/colors';
import { mono, numeric, sans, size as fontSize } from '../../theme/typography';
import { slugOption } from '../../lib/format';
import { addOption } from '../../services/config/config.service';
import { LIST_META, type ListKind } from '../../schemas/config';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface OptionValueModalProps {
  listKind: ListKind;
}

export function OptionValueModal({ listKind }: OptionValueModalProps) {
  const { closeModal } = useShell();
  const { optionsByKind, reload } = useConfig();
  const [raw, setRaw] = useState('');

  const meta = LIST_META[listKind];
  const stored = useMemo(() => slugOption(listKind, raw), [listKind, raw]);
  const existing = optionsByKind[listKind];

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (!raw.trim()) return 'Type a value first.';
      if (!stored) return 'That value has no usable characters.';
      if (existing.some((option) => option.value === stored)) return 'Already on the list.';
      return null;
    },
    submit: () =>
      addOption({
        list_kind: listKind,
        value: stored,
        // The typed form is kept as the display name, so "Card linked" reads
        // back as typed even though it is stored as `card_linked`.
        ...(raw.trim() !== stored ? { display_name: raw.trim() } : {}),
      }),
    title: `${raw.trim()} added`,
    fallback: 'It is now offered in the picker.',
    tone: 'plain',
    onDone: () => {
      void reload();
    },
  });

  return (
    <Modal
      visible
      title={`Add to ${meta.label.toLowerCase()}`}
      hint={meta.note}
      onClose={closeModal}
      primaryLabel="Add"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Input
        label="Value"
        required
        value={raw}
        onChangeText={setRaw}
        placeholder={listKind === 'currency' ? 'SAR' : listKind === 'approver' ? 'Regional manager' : 'Card linked'}
        monospace={listKind === 'currency'}
        autoCapitalize={listKind === 'currency' ? 'characters' : 'sentences'}
      />

      {/* Show what will actually be stored — see the header comment. */}
      {!!stored && stored !== raw.trim() && (
        <View style={styles.preview}>
          <Text style={styles.previewLabel}>Stored as</Text>
          <Text style={styles.previewValue}>{stored}</Text>
        </View>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  preview: { flexDirection: 'row', alignItems: 'baseline', gap: 8 },
  previewLabel: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  previewValue: { ...mono('500'), fontSize: fontSize.label, ...numeric, color: neutral.ink },
});

export default OptionValueModal;
